import { useState, useRef, useCallback, useEffect } from 'react';
import { X, Loader2, ChevronDown } from 'lucide-react';
import type { SearchEntityType, SearchResult } from '@peles/shared';
import { useSearch } from '../../hooks/useSearch';

export interface SearchableSelectProps {
  entityType: SearchEntityType;
  value: string | string[];
  onChange: (value: string | string[]) => void;
  placeholder?: string;
  multiple?: boolean;
  disabled?: boolean;
}

export function SearchableSelect({
  entityType,
  value,
  onChange,
  placeholder = 'Search...',
  multiple = false,
  disabled = false,
}: SearchableSelectProps) {
  const { inputValue, setInputValue, results, isLoading } = useSearch(entityType);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [selectedLabels, setSelectedLabels] = useState<Map<string, SearchResult>>(new Map());
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Track selected labels from search results
  useEffect(() => {
    for (const result of results) {
      if ((Array.isArray(value) && value.includes(result.id)) || value === result.id) {
        setSelectedLabels((prev) => {
          const next = new Map(prev);
          next.set(result.id, result);
          return next;
        });
      }
    }
  }, [results, value]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
      setIsOpen(true);
      setHighlightedIndex(-1);
    },
    [setInputValue],
  );

  const handleSelect = useCallback(
    (result: SearchResult) => {
      setSelectedLabels((prev) => {
        const next = new Map(prev);
        next.set(result.id, result);
        return next;
      });

      if (multiple) {
        const currentValues = Array.isArray(value) ? value : value ? [value] : [];
        if (!currentValues.includes(result.id)) {
          onChange([...currentValues, result.id]);
        }
      } else {
        onChange(result.id);
        setIsOpen(false);
        setInputValue('');
      }
      setHighlightedIndex(-1);
    },
    [multiple, value, onChange, setInputValue],
  );

  const handleRemove = useCallback(
    (idToRemove: string) => {
      if (multiple) {
        const currentValues = Array.isArray(value) ? value : [];
        onChange(currentValues.filter((v) => v !== idToRemove));
      } else {
        onChange('');
      }
    },
    [multiple, value, onChange],
  );

  const handleClear = useCallback(() => {
    if (multiple) {
      onChange([]);
    } else {
      onChange('');
    }
    setInputValue('');
    setSelectedLabels(new Map());
  }, [multiple, onChange, setInputValue]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        setHighlightedIndex(-1);
        return;
      }

      if (!isOpen && (e.key === 'ArrowDown' || e.key === 'Enter')) {
        setIsOpen(true);
        return;
      }

      if (!isOpen) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < results.length - 1 ? prev + 1 : 0));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : results.length - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < results.length) {
          handleSelect(results[highlightedIndex]);
        }
      }
    },
    [isOpen, results, highlightedIndex, handleSelect],
  );

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const items = dropdownRef.current.querySelectorAll('li');
      const item = items[highlightedIndex];
      if (item && typeof item.scrollIntoView === 'function') {
        item.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  const selectedIds = multiple
    ? Array.isArray(value)
      ? value
      : []
    : value
      ? [value as string]
      : [];

  const hasValue = selectedIds.length > 0;

  return (
    <div ref={containerRef} className="relative">
      {/* Selected chips */}
      {hasValue && (
        <div className="flex flex-wrap gap-1 mb-1">
          {selectedIds.map((id) => {
            const label = selectedLabels.get(id);
            return (
              <span
                key={id}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-100 text-primary-800 text-xs rounded-full"
              >
                {label?.label || id}
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => handleRemove(id)}
                    className="hover:text-primary-900"
                    aria-label={`Remove ${label?.label || id}`}
                  >
                    <X size={12} />
                  </button>
                )}
              </span>
            );
          })}
        </div>
      )}

      {/* Search input */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => inputValue.trim() && setIsOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full px-3 py-2 pr-16 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
          role="combobox"
          aria-expanded={isOpen}
          aria-autocomplete="list"
          aria-controls="searchable-select-list"
          aria-label={`Search ${entityType}`}
          autoComplete="off"
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {isLoading && <Loader2 size={14} className="animate-spin text-gray-400" />}
          {hasValue && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Clear selection"
            >
              <X size={14} />
            </button>
          )}
          <ChevronDown size={14} className="text-gray-400" />
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <ul
          id="searchable-select-list"
          ref={dropdownRef}
          role="listbox"
          className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {results.length === 0 && !isLoading && inputValue.trim() && (
            <li className="px-3 py-2 text-sm text-gray-500">No results found</li>
          )}
          {results.length === 0 && !isLoading && !inputValue.trim() && (
            <li className="px-3 py-2 text-sm text-gray-500">Type to search...</li>
          )}
          {results.map((result, index) => {
            const isSelected = selectedIds.includes(result.id);
            return (
              <li
                key={result.id}
                role="option"
                aria-selected={highlightedIndex === index}
                className={`px-3 py-2 cursor-pointer text-sm ${
                  highlightedIndex === index
                    ? 'bg-primary-50 text-primary-900'
                    : isSelected
                      ? 'bg-gray-50 text-gray-700'
                      : 'hover:bg-gray-50 text-gray-900'
                }`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(result);
                }}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                <div className="font-medium">{result.label}</div>
                {result.subtitle && <div className="text-xs text-gray-500">{result.subtitle}</div>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
